import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type DiscountStatus = 'applied' | 'voided';
export type DiscountType = 'flat' | 'percentage';

export interface Discount {
  id: string;
  tenantId: string;
  discountNumber: string;
  clientId: string;
  clientName: string;
  createdByName: string;
  invoiceId: string | null;
  practiceEntityId: string | null;
  amount: number;
  discountType: DiscountType;
  reason: string | null;
  serviceTypeCode: string | null;
  jobId: string | null;
  transactionDate: string;
  dueDate: string | null;
  status: DiscountStatus;
  voidedAt: string | null;
  voidedReason: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

export interface DiscountListParams {
  clientId?: string;
  invoiceId?: string;
  status?: DiscountStatus;
  practiceEntityId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateDiscountPayload {
  client_id: string;
  invoice_id?: string | null;
  amount: number;
  discount_type?: DiscountType;
  reason?: string | null;
  service_type_code?: string | null;
  job_id?: string | null;
  practice_entity_id?: string | null;
  transaction_date?: string;
  due_date?: string | null;
}

export interface VoidDiscountPayload {
  reason: string;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const discountsKeys = {
  all: ['billing', 'discounts'] as const,
  lists: () => [...discountsKeys.all, 'list'] as const,
  list: (params?: DiscountListParams) => [...discountsKeys.lists(), params] as const,
  detail: (id: string) => [...discountsKeys.all, 'detail', id] as const,
};

// ─── Mapper ─────────────────────────────────────────────────────────────────

function mapDiscount(raw: Record<string, unknown>): Discount {
  return {
    id: raw.id as string,
    tenantId: (raw.tenant_id || raw.tenantId || '') as string,
    discountNumber: (raw.discount_number || raw.discountNumber || '') as string,
    clientId: (raw.client_id || raw.clientId || '') as string,
    clientName: (raw.client_name || raw.clientName || '') as string,
    createdByName: (raw.created_by_name || raw.createdByName || '') as string,
    invoiceId: (raw.invoice_id ?? raw.invoiceId ?? null) as string | null,
    practiceEntityId: (raw.practice_entity_id ?? raw.practiceEntityId ?? null) as string | null,
    amount: Number(raw.amount || 0),
    discountType: (raw.discount_type || raw.discountType || 'flat') as DiscountType,
    reason: (raw.reason ?? null) as string | null,
    serviceTypeCode: (raw.service_type_code ?? raw.serviceTypeCode ?? null) as string | null,
    jobId: (raw.job_id ?? raw.jobId ?? null) as string | null,
    transactionDate: (raw.transaction_date || raw.transactionDate || '') as string,
    dueDate: (raw.due_date ?? raw.dueDate ?? null) as string | null,
    status: (raw.status || 'applied') as DiscountStatus,
    voidedAt: (raw.voided_at ?? raw.voidedAt ?? null) as string | null,
    voidedReason: (raw.voided_reason ?? raw.voidedReason ?? null) as string | null,
    createdById: (raw.created_by_id || raw.createdById || '') as string,
    createdAt: (raw.created_at || raw.createdAt || '') as string,
    updatedAt: (raw.updated_at || raw.updatedAt || '') as string,
  };
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch paginated list of discounts.
 */
export function useDiscounts(params: DiscountListParams = {}) {
  return useQuery({
    queryKey: discountsKeys.list(params),
    queryFn: async () => {
      const queryParams: Record<string, string | number> = {};
      if (params.clientId) queryParams.client_id = params.clientId;
      if (params.invoiceId) queryParams.invoice_id = params.invoiceId;
      if (params.status) queryParams.status = params.status;
      if (params.practiceEntityId) queryParams.practice_entity_id = params.practiceEntityId;
      if (params.dateFrom) queryParams.date_from = params.dateFrom;
      if (params.dateTo) queryParams.date_to = params.dateTo;
      if (params.page) queryParams.page = params.page;
      if (params.pageSize) queryParams.pageSize = params.pageSize;

      const response = await apiClient.get<
        ApiResponse<{ discounts: Record<string, unknown>[]; total: number }>
      >('/billing/discounts', { params: queryParams });

      const data = response.data.data;
      return {
        discounts: (data.discounts || []).map(mapDiscount),
        total: data.total || 0,
      };
    },
    retry: false,
  });
}

/**
 * Fetch a single discount by ID.
 */
export function useDiscount(id: string) {
  return useQuery({
    queryKey: discountsKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>>>(
        `/billing/discounts/${id}`,
      );
      return mapDiscount(response.data.data);
    },
    enabled: !!id,
  });
}

/**
 * Create a standalone discount.
 */
export function useCreateDiscount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateDiscountPayload) => {
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
        '/billing/discounts',
        data,
      );
      return mapDiscount(response.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: discountsKeys.all });
      queryClient.invalidateQueries({ queryKey: ['billing', 'invoices'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'client-ledger'] });
    },
  });
}

/**
 * Void a discount.
 */
export function useVoidDiscount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: VoidDiscountPayload }) => {
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
        `/billing/discounts/${id}/void`,
        data,
      );
      return mapDiscount(response.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: discountsKeys.all });
      queryClient.invalidateQueries({ queryKey: ['billing', 'invoices'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'client-ledger'] });
    },
  });
}
